import { insertAllAdvertBanners } from "@modules/azm/advert-banners.ts"
import { ladiesStarsLogoIntersection } from "@modules/azm/ladies-stars-separation-azm.ts"
import { replaceBreadcrumb } from "@modules/breadcrumb/breadcrumb"
import {
    updateAdvertCardContainerUI,
    updateAdvertResultsFoundInfoInDom,
} from "@modules/global/advert-card-container.ts"
import { setAZMFilterPathToSessionStorage } from "@modules/global/filter-path/filter-path-handler.ts"
import { topStarsStripeChangeVisibility } from "@modules/stars/top-stars-stripe"
import { hideTopAdverts, showTopAdverts } from "@modules/top-adverts/top-adverts"
import type { ApiFiltersResult } from "@modules/types"
import { isPC } from "@modules/util/device.ts"
import { dispatchGlobalEvent } from "@modules/util/events"
import { updateLinks } from "@modules/util/update-links"
import { updateUrl } from "@modules/util/url"
import { advertCardsContainerElement } from "../global/dom.ts"
import { addMerkzettelIconToAdvertCards } from "./advert-card-merkzettel-highlighting"
import { hideResultsTopBar, showResultsTopBar } from "./advert-card-view-type"
import { updateAffiliateAdvertsInDom } from "./affiliates"
import { isActiveStarsFilter } from "./filter"
import { updateActiveFilterBarInDom } from "./filter-selection/filter-bar"
import { setShowResultsButton } from "./filter-selection/set-show-results-button"
import {
    getNextPage,
    loadNextAdvertsButton,
    loadNextAdvertsButtonBottom,
    loadPreviousAdvertsButton,
    loadPreviousAdvertsButtonBottom,
    setPagination,
    updatePaginationButtons,
    updatePaginationSelect,
} from "./pagination/pagination"
import { resetAdvertsForUserAndNotify } from "./snapshot-updated-notification"

export function removeSamePageContent() {
    if (isPC()) {
        const container = advertCardsContainerElement()
        if (container) {
            container.innerHTML = ""
        }
        return
    }
    const pageStartElement = advertCardsContainerElement()?.querySelector(`#page-section-${getNextPage() - 1}`)
    let nextElement = pageStartElement?.nextElementSibling
    while (continueRemovingSamePageElement(nextElement)) {
        const oldEl = nextElement
        nextElement = nextElement?.nextElementSibling
        oldEl?.remove()
    }
    pageStartElement?.remove()
}

export function continueRemovingSamePageElement(el: Element | null | undefined) {
    if (!el) {
        return false
    }
    return !el.id.includes("page-section")
}

// TODO via callbacks or event based.
export async function afterFilterApiFetched(
    filterApiResult: ApiFiltersResult | undefined,
    options: { insertAdjacentHTML?: InsertPosition; removeSamePageContent?: boolean } = {},
) {
    if (!filterApiResult) {
        return
    }
    dispatchGlobalEvent("azm.filter-api.after-fetch", filterApiResult)
    const advertCardsContainerEl = advertCardsContainerElement()
    if (advertCardsContainerEl) {
        await new Promise((resolve) =>
            requestAnimationFrame(() => {
                if (options.removeSamePageContent) {
                    removeSamePageContent()
                }
                if (options.insertAdjacentHTML) {
                    advertCardsContainerEl.insertAdjacentHTML(options.insertAdjacentHTML, filterApiResult.content)
                } else {
                    advertCardsContainerEl.innerHTML = filterApiResult.content
                }
                resolve(null)
            }),
        )
    }

    updateAdvertCardContainerUI(filterApiResult.advertCount)
    replaceBreadcrumb(filterApiResult.breadcrumb)
    updateUrl(filterApiResult.seoUrl)
    setPagination(filterApiResult.pagination)
    setShowResultsButton(filterApiResult.advertCount || 0)
    updatePaginationButtons(filterApiResult.pagination)
    updatePaginationSelect(filterApiResult.pagination)
    addMerkzettelIconToAdvertCards()
    topStarsStripeChangeVisibility(!isActiveStarsFilter())
    ladiesStarsLogoIntersection(isActiveStarsFilter())
    updateActiveFilterBarInDom()
    updateAdvertResultsFoundInfoInDom(filterApiResult)
    updateAffiliateAdvertsInDom()
    updateLinks()
    insertAllAdvertBanners(true)

    setAZMFilterPathToSessionStorage(filterApiResult, "azm")

    if (filterApiResult.advertCount > 0) {
        showResultsTopBar()
        showTopAdverts()
    } else {
        hideResultsTopBar()
        hideTopAdverts()
    }

    SiteInfo.endsWithLastAdvertInGroup = filterApiResult.endsWithLastAdvertInGroup || false
    SiteInfo.currentSearchHash = filterApiResult.currentSearchHash || ""
    SiteInfo.seoHrefPreviousUrl = filterApiResult.seoHrefPreviousUrl || ""
    SiteInfo.seoHrefNextUrl = filterApiResult.seoHrefNextUrl || ""

    updateLoadNextAdvertsButtonHrefFromString(SiteInfo.seoHrefNextUrl)
    updateLoadPreviousAdvertsButtonHrefFromString(SiteInfo.seoHrefPreviousUrl)

    if (filterApiResult.snapshotUpdatedDueToNewDay || filterApiResult.snapshotUpdatedDueToNewIndex) {
        resetAdvertsForUserAndNotify()
    }
}

function updateLoadNextAdvertsButtonHrefFromString(seoHrefNextUrl: string) {
    loadNextAdvertsButton()?.closest("a")?.setAttribute("href", seoHrefNextUrl)
    loadNextAdvertsButtonBottom()?.closest("a")?.setAttribute("href", seoHrefNextUrl)
}
function updateLoadPreviousAdvertsButtonHrefFromString(seoHrefPreviousUrl: string) {
    loadPreviousAdvertsButton()?.closest("a")?.setAttribute("href", seoHrefPreviousUrl)
    loadPreviousAdvertsButtonBottom()?.closest("a")?.setAttribute("href", seoHrefPreviousUrl)
}
